import * as React from 'react';
import PropTypes from 'prop-types';
import { useTheme, styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import SearchIcon from '@mui/icons-material/Search';
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import InputBase from '@mui/material/InputBase';
import OutlinedInput from '@mui/material/OutlinedInput';
import StartAdornment from '@mui/material/InputAdornment';

const StyledAutocompletePopper = styled('div')(({ theme }) => ({
  [`& .${autocompleteClasses.paper}`]: {
    boxShadow: 'none',
    margin: 0,
    color: 'inherit',
    fontSize: 13,
    borderRadius: 0,
  },
  [`& .${autocompleteClasses.listbox}`]: {
    backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1c2128',
    padding: 0,
    [`& .${autocompleteClasses.option}`]: {
      minHeight: 'auto',
      alignItems: 'flex-start',
      padding: 8,
      borderBottom: `1px solid  ${
        theme.palette.mode === 'light' ? ' #eaecef' : '#30363d'
      }`,
      [`&.${autocompleteClasses.focused}, &.${autocompleteClasses.focused}[aria-selected="true"]`]:
        {
          backgroundColor: theme.palette.action.hover,
        },
    },
  },
  [`&.${autocompleteClasses.popperDisablePortal}`]: {
    position: 'relative',
  },
}));

function PopperComponent(props) {
  const { disablePortal, anchorEl, open, ...other } = props;
  return <StyledAutocompletePopper {...other} />;
}

PopperComponent.propTypes = {
  anchorEl: PropTypes.any,
  disablePortal: PropTypes.bool,
  open: PropTypes.bool.isRequired,
};

const StyledPopper = styled(Popper)(({ theme, width }) => ({
  'border': `1px solid ${
    theme.palette.mode === 'light' ? '#e1e4e8' : '#30363d'
  }`,
  'boxShadow': `0 8px 24px ${
    theme.palette.mode === 'light' ? 'rgba(149, 157, 165, 0.2)' : 'rgb(1, 4, 9)'
  }`,
  'borderRadius': 6,
  width,
  'zIndex': theme.zIndex.modal,
  'fontSize': 13,
  'color': theme.palette.mode === 'light' ? '#24292e' : '#c9d1d9',
  'backgroundColor': theme.palette.mode === 'light' ? '#fff' : '#1c2128',
  '& .MuiAutocomplete-popper': {
    width: '100% !important',
  },
}));

export default function Picker({
  options,
  value,
  onChange,
  children,
  placeholder,
  title,
  width = 300,
  multiple = true,
  placement = 'bottom-start',
  renderFooter,
  ...rest
}) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [pendingValue, setPendingValue] = React.useState([]);
  const theme = useTheme();

  const handleClick = (event) => {
    setPendingValue(value);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    onChange(pendingValue);
    onClose();
  };

  const handleChange = (val, reason) => {
    console.log(val, reason);
  };

  const onClose = () => {
    if (anchorEl) {
      anchorEl.focus();
    }
    setAnchorEl(null);
  };

  const clearAll = () => {
    setPendingValue([]);
  };

  const open = Boolean(anchorEl);

  return (
    <React.Fragment>
      {React.cloneElement(children, { onClick: handleClick })}
      <StyledPopper
        open={open}
        anchorEl={anchorEl}
        placement={placement}
        width={width}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <div>
            {title && (
              <Box
                sx={{
                  borderBottom: `1px solid ${
                    theme.palette.mode === 'light' ? '#eaecef' : '#30363d'
                  }`,
                  padding: '8px 10px',
                  fontWeight: 600,
                }}
              >
                {title}
              </Box>
            )}
            <Autocomplete
              open
              multiple={multiple}
              onClose={(event, reason) => {
                if (reason === 'escape') {
                  handleClose();
                }
              }}
              value={multiple ? pendingValue : value}
              onChange={(event, newValue, reason) => {
                if (
                  event.type === 'keydown' &&
                  event.key === 'Backspace' &&
                  reason === 'removeOption'
                ) {
                  return;
                } else if (reason === 'clear') {
                  return;
                }
                setPendingValue(newValue);
                if (!multiple) {
                  handleClose();
                  onChange(newValue);
                }
              }}
              disableCloseOnSelect={multiple}
              PopperComponent={PopperComponent}
              renderTags={() => null}
              renderOption={(props, option, { selected }) =>
                option.render ? (
                  <li {...props} key={option.value}>
                    {option.render(option)}
                  </li>
                ) : (
                  <li {...props} key={option.value}>
                    {multiple && (
                      <Box
                        component={DoneIcon}
                        sx={{ width: 17, height: 17, mr: '5px', ml: '-2px' }}
                        style={{
                          visibility: selected ? 'visible' : 'hidden',
                        }}
                      />
                    )}
                    <Box
                      sx={{
                        'flexGrow': 1,
                        '& span': {
                          color:
                            theme.palette.mode === 'light'
                              ? '#586069'
                              : '#8b949e',
                        },
                      }}
                    >
                      {option.label}
                      {option.description && (
                        <>
                          <br />
                          <span>{option.description}</span>
                        </>
                      )}
                    </Box>
                    {multiple && (
                      <Box
                        component={CloseIcon}
                        sx={{ opacity: 0.6, width: 18, height: 18 }}
                        style={{
                          visibility: selected ? 'visible' : 'hidden',
                        }}
                      />
                    )}
                  </li>
                )
              }
              options={options}
              getOptionLabel={(option) => option.label}
              renderInput={(params) => (
                <Box p={1} borderBottom={1} borderColor="divider">
                  <OutlinedInput
                    dx={{ pr: 0 }}
                    size="small"
                    startAdornment={
                      <StartAdornment position="start">
                        <SearchIcon color="primary" />
                      </StartAdornment>
                    }
                    ref={params.InputProps.ref}
                    inputProps={params.inputProps}
                    autoFocus
                    placeholder={placeholder}
                  />
                </Box>
              )}
              {...rest}
            />
            {renderFooter && renderFooter({ onClose, clearAll })}
          </div>
        </ClickAwayListener>
      </StyledPopper>
    </React.Fragment>
  );
}
